import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SchedulerLogsTable from './SchedulerLogsTable';
import { SchedulerLog, SchedulerLogFilters, SchedulerConfig } from '@/types/smsScheduler';

interface SchedulerLogsDialogProps {
  open: boolean;
  onClose: () => void;
  logs: SchedulerLog[];
  configurations: SchedulerConfig[];
  loading?: boolean;
  error?: string | null;
  onRefresh: (filters: SchedulerLogFilters) => void;
}

const SchedulerLogsDialog: React.FC<SchedulerLogsDialogProps> = ({
  open,
  onClose,
  logs,
  configurations,
  loading = false,
  error = null,
  onRefresh
}) => {
  const [filters, setFilters] = useState<SchedulerLogFilters>({
    limit: 100
  });

  const [localFilters, setLocalFilters] = useState<SchedulerLogFilters>({
    limit: 100
  });

  useEffect(() => {
    if (open) {
      // Reset filters when dialog opens
      setLocalFilters({ limit: 100 });
      setFilters({ limit: 100 });
      onRefresh({ limit: 100 });
    }
  }, [open, onRefresh]);

  const handleFilterChange = (field: keyof SchedulerLogFilters) => (
    event: any
  ) => {
    const value = event.target ? event.target.value : event;
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const applyFilters = () => {
    setFilters(localFilters);
    onRefresh(localFilters);
  };

  const clearFilters = () => {
    const clearedFilters = { limit: 100 };
    setLocalFilters(clearedFilters);
    setFilters(clearedFilters);
    onRefresh(clearedFilters);
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (localFilters.config_id) count++;
    if (localFilters.schedule_name) count++;
    if (localFilters.status) count++;
    if (localFilters.start_date) count++;
    if (localFilters.end_date) count++;
    return count;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h6" component="span">
                Scheduler Execution Logs
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {getActiveFiltersCount() > 0 && (
                <Chip
                  label={`${getActiveFiltersCount()} filters`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              <Tooltip title="Refresh Logs">
                <IconButton
                  size="small"
                  onClick={() => onRefresh(filters)}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Filters Section */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FilterIcon color="primary" />
              <Typography variant="h6">
                Filters
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Configuration</InputLabel>
                  <Select
                    value={localFilters.config_id || ''}
                    onChange={handleFilterChange('config_id')}
                    label="Configuration"
                  >
                    <MenuItem value="">
                      <em>All Configurations</em>
                    </MenuItem>
                    {configurations.map((config) => (
                      <MenuItem key={config.id} value={config.id}>
                        {config.schedule_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Schedule Name"
                  value={localFilters.schedule_name || ''}
                  onChange={handleFilterChange('schedule_name')}
                  fullWidth
                  size="small"
                  placeholder="Search by name"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={localFilters.status || ''}
                    onChange={handleFilterChange('status')}
                    label="Status"
                  >
                    <MenuItem value="">
                      <em>All Statuses</em>
                    </MenuItem>
                    <MenuItem value="started">Started</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Limit"
                  type="number"
                  value={localFilters.limit || 100}
                  onChange={handleFilterChange('limit')}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={localFilters.start_date ? new Date(localFilters.start_date) : null}
                  onChange={(date) => handleFilterChange('start_date')(date?.toISOString().split('T')[0])}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={localFilters.end_date ? new Date(localFilters.end_date) : null}
                  onChange={(date) => handleFilterChange('end_date')(date?.toISOString().split('T')[0])}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={applyFilters}
                    startIcon={<FilterIcon />}
                    size="small"
                  >
                    Apply Filters
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                    size="small"
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Logs Table */}
          <SchedulerLogsTable
            logs={logs}
            loading={loading}
            error={error}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SchedulerLogsDialog;

